<template>
    <v-card class="my-2" :elevation="!fullCard ? 0 : undefined" variant="outlined">
        <v-list-item lines="three">
            <div class="d-flex flex-column flex-md-row">
                <div class="align-self-start" :class="{'pl-2': !fullCard}">
                    <v-list-item-title class="wrap-words">
                        <div
                            class="wrap-words d-flex align-center text-subtitle-1 font-weight-bold cursor-pointer mb-1"
                            @click="offerInfo()"
                        >
                            <v-icon size="small" start>mdi-information-outline</v-icon>
                            <span>
                                {{ `${offer.serviceName} / ${offer.categoryName}` }}
                            </span>
                            <ProductOfferTipsDialog v-if="offer.tipsAvailable" :offer-key="offer.offerKey" />
                        </div>
                        <div v-if="discountDetails" class="d-flex flex-shrink-0 width-max-content">
                            <special-offer-label :details="discountDetails" />
                        </div>
                        <div v-if="offer.available" class="d-flex align-center text-caption">
                            <v-icon :title="$t('available')" start size="small" class="text-success">
                                mdi-checkbox-marked-circle
                            </v-icon>
                            <span class="text-success" v-text="$t('available')" />
                        </div>
                        <div v-else class="d-flex align-center text-caption">
                            <v-icon :title="$t('on_request')" start size="small" class="text-warning"
                                >mdi-alert-circle</v-icon
                            >
                            <span class="text-warning" v-text="$t('on_request')" />
                        </div>
                        <cruise-meal-type-label v-if="offer.mealType" :offer="offer" />
                        <div class="d-flex align-center text-caption">
                            <v-icon size="small" start>mdi-calendar</v-icon>
                            <span>
                                {{ `${$filters.dateFormat(offer.startDate)}` }}
                            </span>
                        </div>
                        <discount-label v-if="offer.price" :price="offer.price" class="d-inline-block" />
                        <span v-if="offer && offer.price" class="text-caption">
                            <conditions-label
                                :offer-key="offer.offerKey"
                                :price="offer.price"
                                @conditions="showConditionsInfo"
                            />
                        </span>
                        <div v-if="bedTypeText" class="font-italic text-caption">
                            {{ bedTypeText }}
                        </div>
                        <div v-if="offer.tariffName" class="d-flex align-center text-caption">
                            <v-icon start size="small" class="text-info">mdi-tag-text</v-icon>
                            <span>{{ offer.tariffName }}</span>
                        </div>
                    </v-list-item-title>
                </div>
                <v-spacer />
                <div class="offer-price align-self-end align-self-md-center d-flex flex-column align-center">
                    <slot name="price" :offer="offer">
                        <template v-if="!offer.soldOut">
                            <supplier-price v-if="!isAgency" :supplier-price="offer.supplierPrice" />
                            <v-btn
                                variant="flat"
                                color="primary"
                                size="small"
                                no-prefetch
                                block
                                height="36px"
                                max-height="36px"
                                :loading="!info.shipName"
                                :disabled="!info.shipName"
                                @click="selectForBooking"
                            >
                                {{ $filters.price(offer.price) }}
                            </v-btn>
                            <price-note :search-request="searchRequest" />
                            <commission-amount :commission="offer.price.commission" />
                        </template>
                        <sold-out-modal v-else small />
                    </slot>
                </div>
            </div>
        </v-list-item>
    </v-card>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import CommissionAmount from '@/components/snippets/CommissionAmount.vue'
    import DiscountLabel from '~src/components/snippets/discountLabel.src'
    import ConditionsLabel from '~src/components/snippets/conditionsLabel.src'
    import {EventBus, SHOW_CRUISE_CONDITIONS_INFO, SHOW_CRUISE_OFFER_INFO_MODAL} from '@/utils/event-bus'
    import PriceNote from '@/components/snippets/PriceNote.vue'
    import CruiseMealTypeLabel from '@/components/hotels/snippets/CruiseMealTypeLabel.vue'
    import SpecialOfferLabel from '~/components/hotels/search/offers/SpecialOfferLabel.vue'
    import SupplierPrice from '~src/components/snippets/supplierPrice.src'
    import ProductOfferTipsDialog from '~/components/modals/ProductOfferTipsDialog.vue'
    import SoldOutModal from '~src/components/snippets/SoldOutModal.vue'

    @Component({
        components: {
            SoldOutModal,
            ProductOfferTipsDialog,
            SpecialOfferLabel,
            CruiseMealTypeLabel,
            PriceNote,
            CommissionAmount,
            DiscountLabel,
            ConditionsLabel,
            SupplierPrice,
        },
    })
    export class CruiseOfferCard extends Vue {
        @Prop({required: true}) offer
        @Prop({default: true}) fullCard

        imgError = false
        info = {
            images: [],
        }

        async mounted() {
            try {
                this.info = await this.$api.cruiseInfo.get({offerKey: this.offer.offerKey})
                if (this.fullCard) {
                    this.$emit('info', this.info)
                }
                if (this.fullCard && this.info.images) {
                    this.$emit('images', this.info.images)
                }
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        get bedTypeText() {
            if (this.offer.onExtraBed && this.offer.onWithoutPlace) {
                return this.$t('accommodated_on_extra_bed_or_without_bed')
            } else if (this.offer.onExtraBed && !this.offer.onWithoutPlace) {
                return this.$t('accommodated_on_extra_bed')
            } else if (!this.offer.onExtraBed && this.offer.onWithoutPlace) {
                return this.$t('accommodated_without_bed')
            }

            return ''
        }

        onImgError() {
            if (this.mainImage.url) {
                this.imgError = true
            }
        }

        selectForBooking() {
            cruisesStore.ADD_TO_BASKET({info: this.info, offerKey: this.offer.offerKey})
            this.$router.push({
                name: 'cruiseBooking',
                query: {offerKey: this.offer.offerKey},
            })
        }

        offerInfo() {
            EventBus.$emit(SHOW_CRUISE_OFFER_INFO_MODAL, {offer: this.offer})
        }

        showConditionsInfo() {
            EventBus.$emit(SHOW_CRUISE_CONDITIONS_INFO, this.offer)
        }

        get mainImage() {
            let mainImage = this.info.images.find(image => image.mainImage) || {url: null}
            if (!mainImage.url && this.info.images.length > 0) {
                mainImage = this.info.images[0]
            }
            return mainImage
        }

        get searchRequest() {
            return cruisesStore.searchRequest
        }

        get isAgency() {
            return authStore.isAgency
        }

        get discountDetails() {
            return this.offer?.price?.discountDetails
        }
    }

    export default CruiseOfferCard
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';

    .offer-price {
        width: 20%;
    }

    @media #{map.get(settings.$display-breakpoints, 'xs')} {
        .offer-price {
            width: 25%;
        }
    }

    .wrap-words {
        white-space: normal;
    }
</style>
